import type { ICSPRClickSDK, SendResult } from '@make-software/csprclick-core-types';
import {
  CasperNetwork,
  ContractHash,
  Deploy,
  DeployHeader,
  ExecutableDeployItem,
  Hash,
  PublicKey,
  SessionBuilder,
  StoredVersionedContractByHash,
  sleep,
  type Args,
  type RpcClient,
  type Transaction,
} from 'casper-js-sdk';

import { TTL } from 'utils';

import { requireCasperChain } from './rpcClient';

export interface TransactionCallbacks {
  onSent?: (hash: string) => void;
  onProcessed?: () => void;
  onError?: (error: unknown) => void;
  onCancelled?: () => void;
}

export class TransactionService {
  private casperNetwork: CasperNetwork | null = null;
  private casperNetworkVersion: number | null = null;

  constructor(
    private client: RpcClient,
    private clickRef: ICSPRClickSDK
  ) {}

  /**
   * Building for signing starts here: the node status is read once per session through
   * CSPR.click's proxy, and nothing is built unless that proxy serves `config.cspr_chain_name`.
   */
  private async getCasperNetwork(): Promise<CasperNetwork> {
    const { apiVersion } = await requireCasperChain();
    if (!this.casperNetwork || this.casperNetworkVersion !== apiVersion) {
      this.casperNetwork = new CasperNetwork(this.client, apiVersion);
      this.casperNetworkVersion = apiVersion;
    }

    return this.casperNetwork;
  }

  async executeDeployOrTransaction(
    deployOrTransaction: Deploy | Transaction,
    publicKey: string,
    callbacks: TransactionCallbacks
  ): Promise<void> {
    if (!publicKey) {
      throw Error('No available account to sign');
    }

    const isDeploy = deployOrTransaction instanceof Deploy;

    const statusUpdate = (status: string, data: SendResult) => {
      if (status === 'sent') {
        const hash = isDeploy ? data?.deployHash : data?.transactionHash;
        if (hash) {
          callbacks.onSent?.(hash);
        }
      }
      if (status === 'processed') {
        // Check if there's an error in the SendResult
        if (data?.error || data?.errorData) {
          callbacks.onError?.(data.error || data.errorData);
        } else {
          callbacks.onProcessed?.();
        }
      }
      if (status === 'expired') {
        callbacks.onError?.({ message: 'Transaction expired (TTL elapsed)' });
      }
      if (status === 'timeout') {
        callbacks.onError?.({ message: 'Transaction monitoring timeout' });
      }
      if (status === 'error') {
        callbacks.onError?.(data?.error || data?.errorData || { message: 'Unexpected error occurred' });
      }
    };

    const jsonPayload = isDeploy
      ? JSON.stringify(Deploy.toJSON(deployOrTransaction as Deploy))
      : JSON.stringify({
          transaction: {
            Version1: (deployOrTransaction as Transaction).toJSON(),
          },
        });

    const res = await this.clickRef.send(jsonPayload, publicKey, statusUpdate);

    if (res?.cancelled) {
      callbacks.onCancelled?.();
      callbacks.onError?.({ message: 'Transaction was cancelled' });
      throw new Error('Transaction was cancelled');
    }

    if (res?.error) {
      callbacks.onError?.(res.error);
      throw new Error(res.error);
    }

    await sleep(2000);
  }

  supportsTransactions(): boolean {
    const supportedItems: string[] = this.clickRef?.getActiveAccount()?.['providerSupports'] || [];

    return supportedItems.includes('sign-transactionv1');
  }

  async createContractPackageCallTransaction(
    publicKey: string,
    entryPoint: string,
    standardPayment: string,
    runtimeArgs: Args,
    contractPackageHash: string
  ): Promise<Transaction> {
    const casperNetwork = await this.getCasperNetwork();

    return casperNetwork.createContractPackageCallTransaction(
      PublicKey.fromHex(publicKey),
      contractPackageHash.replace('hash-', ''),
      entryPoint,
      config.cspr_chain_name,
      Number(standardPayment),
      runtimeArgs,
      TTL,
      undefined,
      config.gas_price
    );
  }

  async createSessionWasmTransaction(
    publicKey: string,
    standardPayment: string,
    wasmBinary: Uint8Array,
    runtimeArgs: Args
  ): Promise<Transaction> {
    const { apiVersion } = await requireCasperChain();

    const sessionWasm = new SessionBuilder()
      .from(PublicKey.fromHex(publicKey))
      .chainName(config.cspr_chain_name)
      .payment(Number(standardPayment), config.gas_price)
      .ttl(TTL)
      .wasm(wasmBinary)
      .installOrUpgrade()
      .runtimeArgs(runtimeArgs);

    if (apiVersion === 2) {
      return sessionWasm.build();
    }

    return sessionWasm.buildFor1_5();
  }

  createContractDeploy(
    publicKey: string,
    standardPayment: string,
    entryPoint: string,
    runtimeArgs: Args,
    hash: string
  ) {
    const deployHeader = DeployHeader.default();
    deployHeader.chainName = config.cspr_chain_name;
    deployHeader.account = PublicKey.fromHex(publicKey);
    deployHeader.gasPrice = config.gas_price;

    const decodedHash = new ContractHash(Hash.fromHex(hash.replace('hash-', '')), '');

    const payment = ExecutableDeployItem.standardPayment(standardPayment);

    const session = new ExecutableDeployItem();
    session.storedVersionedContractByHash = new StoredVersionedContractByHash(decodedHash, entryPoint, runtimeArgs);

    return Deploy.makeDeploy(deployHeader, payment, session);
  }

  createWASMContractDeploy(publicKey: string, standardPayment: string, paymentBinary: Uint8Array, runtimeArgs: Args) {
    const deployHeader = DeployHeader.default();
    deployHeader.chainName = config.cspr_chain_name;
    deployHeader.account = PublicKey.fromHex(publicKey);
    deployHeader.gasPrice = config.gas_price;

    const payment = ExecutableDeployItem.standardPayment(standardPayment);

    const session = ExecutableDeployItem.newModuleBytes(paymentBinary, runtimeArgs);

    return Deploy.makeDeploy(deployHeader, payment, session);
  }

}
